import {
  LETTERS,
  STRIPCONTAINERHEIGHT,
  SEARCHBARHEIGHT,
  SELECTIONCOUNTROWHEIGHT
} from '../common'
import scrollbarSize from 'dom-helpers/scrollbarSize'
import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { TableViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/TableViewColumnDefinition'
import React from 'react'
import { StandardTableViewStore } from '../stores/StandardTableViewStore'
import { useLocalStore } from 'mobx-react-lite'
import { AutoSizer } from 'react-virtualized'
import { getRowWidth, getStickyColumnsStyle } from '@services/tableViewService'
import { SelectionCountRow } from '../components/SelectionCountRow'
import { LetterJumpTo } from '../components/LetterJumper'
import { TableSearch } from '../components/TableSearch'
import { TableHeader } from '../components/TableHeader'
import { TableColumnFilter } from '../components/TableColumnFilter'
import { TableBody } from '../components/TableBody'
import { stores } from '@stores'

interface IStandardTableViewProps {
  data: Array<any>
  fieldNameToJump?: string
  headerHeight: number
  rowHeight: number
  colDef: Array<TableViewColumnDefinition>
  useIsScrolling?: boolean
  overScanCount?: number
  hideHeader?: boolean
  showSearch?: boolean
  searchBarHeight?: number
  hideLetterNavigation?: boolean
  expandableSectionHeight?: number
  expandedRowKey?: string
  expandedRowComponent?: any
  tableCellFontSize?: any
  showBodyRowBorder?: boolean
  allowMultiSelect?: boolean
  maxSelectionLimit?: number
  setSelectedData?: any
  canSelectAll?: boolean
  showCountDetails?: boolean
  selectedRows?: any
  disabledRows?: any[]
  selectedRowsByTab?: any
  hideHeaderTopBorder?: boolean
  setFilterData?: any
  showCountDetailOnly?: boolean
  allowDisabledRowsSelection?: boolean
  isTableDisabled?: boolean
}

export const TableViewStoreContext = React.createContext<StandardTableViewStore | null>(
  null
)

export const StandardTableView: React.FunctionComponent<IStandardTableViewProps> = ({
  data,
  headerHeight,
  rowHeight,
  fieldNameToJump,
  colDef,
  useIsScrolling,
  overScanCount,
  hideHeader,
  showSearch,
  hideLetterNavigation,
  expandableSectionHeight,
  expandedRowKey,
  expandedRowComponent,
  tableCellFontSize,
  showBodyRowBorder,
  allowMultiSelect,
  maxSelectionLimit,
  setSelectedData,
  canSelectAll,
  showCountDetails = true,
  selectedRows,
  disabledRows,
  selectedRowsByTab,
  hideHeaderTopBorder,
  setFilterData,
  showCountDetailOnly,
  allowDisabledRowsSelection = true,
  isTableDisabled
}) => {
  const store = useLocalStore(() => new StandardTableViewStore())
  const {
    nav: {
      queryParams: { section, view }
    }
  } = stores

  React.useEffect(
    () => store.setAllowDisabledRowsSelection(allowDisabledRowsSelection),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowDisabledRowsSelection]
  )

  React.useEffect(() => {
    store.clearSelectedRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, view])

  return (
    <TableViewStoreContext.Provider value={store}>
      <AutoSizer>
        {({ height, width }) => {
          if (width <= 0 || height <= 0) return <></>
          const widthWithoutScroll = width - scrollbarSize()
          const tableBodyVisibleHeight =
            height -
            (allowMultiSelect
              ? hideLetterNavigation
                ? SELECTIONCOUNTROWHEIGHT
                : SELECTIONCOUNTROWHEIGHT - 10
              : 0) -
            (hideLetterNavigation ? 0 : STRIPCONTAINERHEIGHT) -
            (hideHeader && !canSelectAll ? 0 : headerHeight) -
            (showSearch ? SEARCHBARHEIGHT : 0)
          const tableRowWidth = getRowWidth(
            colDef as any[],
            null as any,
            allowMultiSelect as boolean
          )
          const colWiseStickyStyle = getStickyColumnsStyle(
            colDef as any[],
            allowMultiSelect as boolean
          )
          return data && data.length ? (
            <>
              {allowMultiSelect && showCountDetails ? (
                <SelectionCountRow
                  showCountDetailOnly={showCountDetailOnly}
                  setFilterData={setFilterData}
                  maxSelectionLimit={maxSelectionLimit as number}
                  selectedRowsByTab={selectedRowsByTab}
                  height={
                    hideLetterNavigation
                      ? SELECTIONCOUNTROWHEIGHT
                      : SELECTIONCOUNTROWHEIGHT - 10
                  }
                  width={widthWithoutScroll}
                />
              ) : null}
              {!hideLetterNavigation && fieldNameToJump && (
                <LetterJumpTo data={LETTERS} stripWidth={widthWithoutScroll} />
              )}
              {showSearch && <TableSearch width={width} height={SEARCHBARHEIGHT} />}
              {!hideHeader || allowMultiSelect ? (
                <TableHeader
                  visibleHeight={headerHeight}
                  visibleWidth={widthWithoutScroll}
                  rowHeight={headerHeight}
                  rowWidth={tableRowWidth}
                  stickyCols={colWiseStickyStyle}
                  colDef={colDef}
                  allowMultiSelect={allowMultiSelect}
                  hideHeader={hideHeader}
                  maxSelectionLimit={maxSelectionLimit}
                  canSelectAll={canSelectAll}
                  hideHeaderTopBorder={hideHeaderTopBorder}
                  isTableDisabled={isTableDisabled}
                />
              ) : null}
              <TableColumnFilter />
              <TableBody
                overScanCount={overScanCount}
                data={data}
                useIsScrolling={useIsScrolling}
                colDef={colDef}
                visibleHeight={tableBodyVisibleHeight}
                visibleWidth={width}
                rowWidth={tableRowWidth}
                rowHeight={rowHeight}
                expandableSectionHeight={expandableSectionHeight}
                expandedRowKey={expandedRowKey}
                expandedRowComponent={expandedRowComponent}
                stickyCols={colWiseStickyStyle}
                fieldNameToJump={fieldNameToJump}
                showBodyRowBorder={showBodyRowBorder}
                tableCellFontSize={tableCellFontSize}
                allowMultiSelect={allowMultiSelect}
                maxSelectionLimit={maxSelectionLimit}
                setSelectedData={setSelectedData}
                selectedData={selectedRows}
                disabledRowData={disabledRows}
                isTableDisabled={isTableDisabled}
              />
            </>
          ) : (
            <NoRecordFound />
          )
        }}
      </AutoSizer>
    </TableViewStoreContext.Provider>
  )
}
